import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connectSocket } from "../socket";
import SideBar from "./SideBar";

// Add category display names mapping
const categoryDisplayNames = {
  'defects': 'Defects',
  'Product docs': 'Product Docs',
  'case notes': 'Case Notes'
};

// Add category color mapping
const getCategoryColor = (category) => {
  const categoryLower = category.toLowerCase();
  
  if (categoryLower === 'defects') {
    return {
      bg: 'bg-blue-100',
      text: 'text-blue-600',
      iconColor: 'text-blue-600',
      border: 'border-blue-200',
      hover: 'hover:bg-blue-200'
    };
  } else if (categoryLower === 'case notes') {
    return {
      bg: 'bg-purple-100',
      text: 'text-purple-600',
      iconColor: 'text-purple-600',
      border: 'border-purple-200',
      hover: 'hover:bg-purple-200'
    };
  } else if (categoryLower === 'product docs') {
    return {
      bg: 'bg-yellow-100',
      text: 'text-yellow-600',
      iconColor: 'text-yellow-600',
      border: 'border-yellow-200',
      hover: 'hover:bg-yellow-200'
    };
  }
  
  // Default fallback
  return {
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    iconColor: 'text-gray-600',
    border: 'border-gray-200',
    hover: 'hover:bg-gray-200'
  };
};

const TSEAgentRag = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [socket, setSocket] = useState(null);
  
  // Search mode states - matching ChatWindow structure
  const [searchMessages, setSearchMessages] = useState([]);
  const [searchSubMode, setSearchSubMode] = useState("rag");
  const [documentSummaries, setDocumentSummaries] = useState({});
  
  const [message, setMessage] = useState("");
  
  // Add support type state with a welcome message - matching ChatWindow
  const [supportType, setSupportType] = useState("WELCOME TO ARTA AI SUPPORT");
  const [displayTitle, setDisplayTitle] = useState("WELCOME TO ARTA AI SUPPORT");
  const [isTyping, setIsTyping] = useState(false);
  const [isTitleSet, setIsTitleSet] = useState(false);
  
  // Add scroll indicator state - matching ChatWindow
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  
  const searchChatBodyRef = useRef(null);
  
  // Force light theme for TSE Dashboard
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Light theme configuration only - matching ChatWindow colors
  const theme = {
    light: {
      bg: 'bg-white',
      sidebarBg: 'bg-gray-50',
      cardBg: 'bg-white',
      headerBg: 'bg-gray-50',
      contentBg: 'bg-white',
      inputBg: 'bg-white',
      text: 'text-[#253439]',
      textSecondary: 'text-gray-600',
      textMuted: 'text-[#A2A9AF]',
      timestampColor: 'text-gray-400',
      border: 'border-[#06b6d4]',
      borderLight: 'border-gray-200',
      hover: 'hover:bg-gray-50',
      buttonBg: 'bg-white',
      buttonHover: 'hover:bg-gray-50',
      accent: 'bg-[#06b6d4]',
      accentHover: 'hover:bg-cyan-700',
      success: 'bg-green-600',
      warning: 'bg-yellow-600',
      danger: 'bg-red-600',
    }
  };
  
  const currentTheme = theme.light;
  
  // Typewriter effect hook - matching ChatWindow
  useEffect(() => {
    if (supportType === displayTitle) return;
    
    setIsTyping(true);
    let currentIndex = 0;
    const targetText = supportType;
    
    const typingInterval = setInterval(() => {
      if (currentIndex <= targetText.length) {
        setDisplayTitle(targetText.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setIsTyping(false);
      }
    }, 50); // Adjust speed here (lower = faster)
    
    return () => clearInterval(typingInterval);
  }, [supportType]);
  
  // Add function to get title from message - matching ChatWindow
  const getTitleFromMessage = (message) => {
    const msg = message.toLowerCase();
    if (msg.includes("network")) return "NETWORK ISSUE";
    if (msg.includes("server")) return "SERVER ISSUE";
    if (msg.includes("graphiant")) return "GRAPHIANT ISSUE";
    return "WELCOME TO ARTA AI SUPPORT"; // Default title when no keywords found
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  // Request document summary function - matching ChatWindow
  const requestDocumentSummary = (documentPath, queryId = null) => {
    if (!documentPath) return;
    
    // Check if we already have a summary for this document
    if (documentSummaries[documentPath]) {
      return;
    }
    
    // Simulate AI summary generation
    setTimeout(() => {
      const mockSummary = `This document provides comprehensive information about ${documentPath.replace('.pdf', '')}. It includes detailed technical specifications, troubleshooting procedures, and best practices for implementation. The content is structured to help users understand complex concepts and apply them in real-world scenarios.`;
      
      setDocumentSummaries(prev => ({
        ...prev,
        [documentPath]: mockSummary
      }));
    }, 2000);
  };

  // Format timestamp function - matching ChatWindow
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  // Format bot message function - matching ChatWindow
  const formatBotMessage = (text, isSearchResponse = false) => {
    if (!text) return "";
    
    let formatted = text;
    
    // Convert line breaks to <br/>
    formatted = formatted.replace(/\n/g, '<br/>');
    
    // Highlight code blocks with backticks
    formatted = formatted.replace(/`([^`]+)`/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
    
    // Highlight values in single quotes
    formatted = formatted.replace(/(?<=\s|^)'([^'\s][^']*?)'(?!\w)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
    
    // Highlight values in double quotes
    formatted = formatted.replace(/(?<=\s|^)"([^"\s][^"]*?)"(?!\w)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
    
    // Highlight values in parentheses
    formatted = formatted.replace(/(?<=\s|^)\(([^\s][^\)]*?)\)(?=\s|[.,;:]|$)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">($1)</span>');
    
    // Highlight values in pipes
    formatted = formatted.replace(/(?<=\s|^)\|([^\|\s][^\|]*?)\|(?=\s|[.,;:]|$)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
    
    // Add search response prefix if this is a search response
    if (isSearchResponse) {
      formatted = `<div style="display: flex; align-items: center; margin-bottom: 8px; color: #2563eb; font-weight: 600; font-size: 12px;">
        <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="margin-right: 6px;">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
        SEARCH RESULT
      </div>${formatted}`;
    }
    
    return formatted;
  };

  // Send search message function - matching ChatWindow structure
  const sendSearchMessage = () => {
    if (!message.trim() || !socket) return;
    
    // Add message to search chat history
    const newMessage = { 
      text: message, 
      sender: "agent", 
      timestamp: Date.now(),
      isSearchQuery: true,
      sources: []
    };
    
    setSearchMessages(prev => [...prev, newMessage]);
    
    // Update title only on first message
    if (!isTitleSet) {
      const newTitle = getTitleFromMessage(message);
      setSupportType(newTitle);
      setIsTitleSet(true);
    }
    
    // Emit search query - matching ChatWindow implementation
    console.log("🔍 Sending search query:", message);
    socket.emit("searchQuery", { text: message });
    
    // Clear message and reset UI
    setMessage("");
    setTimeout(() => {
      const textareas = document.querySelectorAll('textarea');
      textareas.forEach(textarea => {
        textarea.style.height = 'auto';
        textarea.style.height = '28px';
      });
    }, 0);
  };

  // Handle Enter key for search - matching ChatWindow
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendSearchMessage();
    }
  };

  // Auto-resize textarea - matching ChatWindow
  const autoResizeTextarea = (textarea) => {
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
  };

  // Auto-scroll to bottom when new messages arrive - matching ChatWindow implementation
  useEffect(() => {
    if (searchChatBodyRef.current) {
      searchChatBodyRef.current.scrollTop = searchChatBodyRef.current.scrollHeight;
    }
  }, [searchMessages]);

  // Set up scroll event listener and check scroll indicator - matching ChatWindow
  useEffect(() => {
    const container = searchChatBodyRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Check scroll indicator when search messages change - matching ChatWindow
  useEffect(() => {
    // Small delay to ensure DOM is updated
    setTimeout(checkScrollIndicator, 100);
  }, [searchMessages]);

  // Check if scroll indicator should be shown - matching ChatWindow
  const checkScrollIndicator = () => {
    if (!searchChatBodyRef.current) return;
    
    const container = searchChatBodyRef.current;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const scrollTop = container.scrollTop;
    
    // Show indicator if there's more content below the visible area
    const hasMoreContent = scrollHeight > clientHeight && scrollTop < (scrollHeight - clientHeight - 50);
    setShowScrollIndicator(hasMoreContent);
  };

  // Handle scroll events - matching ChatWindow
  const handleScroll = () => {
    checkScrollIndicator();
  };

  // Socket connection - matching ChatWindow
  useEffect(() => {
    const s = connectSocket();
    setSocket(s);

    s.on("connect", () => {
      console.log("✅ Socket connected:", s.id);
      const sessionId = s.id;
      s.emit("joinRoom", { sessionId, role: "agent", name: "Chris" });
    });

    // Listen for search responses (RAG system responses) - matching ChatWindow implementation
    s.on("searchResponse", (msg) => {
      const { text, sender, timestamp, query, rag_sources, complete } = msg;
      console.log("🔍 Search response received:", { text, sender, timestamp, query });

      setSearchMessages((prev) => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;

        // Case 1: Update existing streaming message
        if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
          const lastMsg = updated[lastIdx];

          // Use replace instead of += for controlled updates
          const newText = complete ? lastMsg.text : lastMsg.text + text;

          updated[lastIdx] = {
            ...lastMsg,
            text: newText,
            isStreaming: !complete,
            timestamp: complete ? timestamp : lastMsg.timestamp,
            sources: complete
              ? (rag_sources || []).map((item) => {
                  const relativePath = typeof item === "string" ? item : item.link;
                  const fullUrl = `https://staging-api.artasupport.com/static/images/${relativePath}`;
                  return {
                    link: fullUrl,
                    name: item.name || "No File name found.",
                    summary: item.summary || "No Summary available.",
                    category: item.category || "Product Documents",
                    icon: "pdf",
                  };
                })
              : lastMsg.sources,
          };

          return updated;
        }

        // Case 2: No existing streaming message, so add new one
        return [
          ...prev,
          {
            text,
            sender,
            timestamp,
            isSearchResponse: true,
            isStreaming: !complete,
            sources: (rag_sources || []).map((item) => {
              const relativePath = typeof item === "string" ? item : item.link;
              const fullUrl = `https://staging-api.artasupport.com/static/images/${relativePath}`;
              return {
                link: fullUrl,
                name: item.name || "No File name found.",
                summary: item.summary || "No Summary available.",
                category: item.category || "Product Documents",
                icon: "pdf",
              };
            }),
          },
        ];
      });
    });

    s.on("disconnect", () => {
      console.log("❌ Socket disconnected");
    });

    return () => {
      s.disconnect();
    };
  }, []);

  return (
    <div className="flex h-full w-full transition-colors duration-300 bg-[#F3F3ED]">
      {/* Add typewriter effect CSS - matching ChatWindow */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .typewriter-effect {
            overflow: hidden;
            white-space: nowrap;
            animation: typing 2s steps(40, end);
          }
          
          @keyframes typing {
            from { width: 0 }
            to { width: 100% }
          }
        `
      }} />
      
      <SideBar 
        mode="agent" 
        onLogout={onLogout} 
        isDarkMode={isDarkMode} 
        onToggleTheme={toggleTheme}
        onLinkClick={handleNavigation}
        currentPath={location.pathname}
      />
      
      {/* Chat Title - Move to top left, above the white background - matching ChatWindow */}
      <div className="fixed top-3 left-24 z-[100]">
        <h1 className={`text-sm font-bold text-[#253439] tracking-wide uppercase ${isTyping ? 'after:content-["|"] after:ml-1 after:animate-pulse' : ''}`}
          style={{ letterSpacing: '0.05em' }}>
          {displayTitle}
        </h1>
      </div>

      {/* Move user count to the white background with higher z-index - matching ChatWindow */}
      <div className="fixed top-3 right-8 z-[150] flex items-center gap-2">
        <div className="flex items-center px-3 py-1 rounded-full text-white font-medium shadow bg-[#06b6d4]" style={{ fontSize: 14 }} title="Chris">
          <span>Chris</span>
        </div>
      </div>

      {/* Main content area with Perplexity-like rounded border - matching ChatWindow */}
      <div className="flex-1 flex flex-col relative transition-all duration-300 ease-in-out p-1">
        {/* Rounded container with border - made much larger - matching ChatWindow */}
        <div className="flex-1 bg-white rounded-2xl border-2 border-[#F3F3ED] shadow-sm overflow-hidden relative">
          {/* === CHAT SCROLL AREA === */}
          <div className="flex flex-col flex-1 items-center w-full h-full">
            {/* Fade line at top for messages - matching ChatWindow */}
            <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-white to-transparent z-20 rounded-t-2xl"></div>

            {/* Scroll Indicator - Only show when there's more content - matching ChatWindow */}
            {showScrollIndicator && (
              <div className="absolute bottom-24 right-40 z-50">
                <div className="bg-[#06b6d4] rounded-full shadow-lg border border-[#06b6d4] p-2 animate-bounce">
                  <svg width="16" height="16" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M7 13l5 5 5-5"/>
                    <path d="M7 6l5 5 5-5"/>
                  </svg>
                </div>
              </div>
            )}

            {/* Main content area - matching ChatWindow */}
            <main
              ref={searchChatBodyRef}
              className="w-full max-w-4xl flex-1 overflow-y-auto flex flex-col px-6 py-6 hide-scrollbar"
              style={{
                minHeight: "400px",
                maxHeight: "calc(100vh - 140px)", // Even more available space
                background: "#FFFFFF",
                marginTop: "16px", // Consistent top margin for both modes
                marginBottom: "10px", // Much smaller bottom margin for more space
                position: "relative",
                zIndex: 10
              }}
            >
              <div className="w-full">
                {/* Search Mode - Perplexity-like UI with Multiple Queries - matching ChatWindow */}
                <div className="w-full space-y-8">
                  {/* Empty State for Search - matching ChatWindow */}
                  {searchMessages.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                      <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24" className="mb-4">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="m21 21-4.35-4.35"/>
                      </svg>
                      <p className="text-lg font-medium mb-2">Search the knowledge base</p>
                      <p className="text-sm text-center max-w-md">
                        Ask any question and get answers from our comprehensive knowledge base with cited sources.
                      </p>
                    </div>
                  )}

                  {/* Process search messages to group queries with their responses - matching ChatWindow */}
                  {(() => {
                    const searchGroups = [];
                    let currentGroup = null;
                    
                    searchMessages.forEach((msg, idx) => {
                      if (msg.sender === "agent") {
                        // Start new group with user query
                        if (currentGroup) {
                          searchGroups.push(currentGroup);
                        }
                        currentGroup = {
                          query: msg,
                          responses: [],
                          sources: []
                        };
                      } else if (currentGroup) {
                        // Add response to current group
                        currentGroup.responses.push(msg);
                        if (msg.sources?.length > 0) {
                          currentGroup.sources = msg.sources;
                        }
                      }
                    });
                    
                    // Add the last group if it exists
                    if (currentGroup) {
                      searchGroups.push(currentGroup);
                    }
                    
                    return searchGroups.map((group, groupIdx) => (
                      <div key={groupIdx} className="w-full border-b border-gray-100 pb-8 last:border-b-0">
                        {/* Search Query Display with Typewriter Effect - matching ChatWindow */}
                        <div className="mb-6">
                          <h2 className={`text-2xl font-semibold text-[#253439] mb-2 ${groupIdx === searchGroups.length - 1 ? 'typewriter-effect' : ''}`}>
                            {group.query.text}
                          </h2>
                          
                          {/* Dynamic Search Subtabs - matching ChatWindow */}
                          <div className="flex items-center mb-4 border-b border-gray-200">
                            {/* Answer Tab */}
                            <button
                              onClick={() => setSearchSubMode("rag")}
                              className={`px-4 py-2 text-sm font-medium transition ${
                                searchSubMode === "rag"
                                  ? "border-b-2 border-[#06b6d4] text-[#06b6d4]"
                                  : "text-gray-500 hover:text-[#253439]"
                              }`}
                            >
                              Summary
                            </button>
                            
                            {/* Sources Tab (All Sources) */}
                            <button
                              onClick={() => setSearchSubMode("sources")}
                              className={`px-4 py-2 text-sm font-medium transition ${
                                searchSubMode === "sources"
                                  ? "border-b-2 border-[#06b6d4] text-[#06b6d4]"
                                  : "text-gray-500 hover:text-[#253439]"
                              }`}
                            >
                              Sources ({group.sources?.length || 0})
                            </button>

                            {/* Dynamic Category Tabs - matching ChatWindow */}
                            {(() => {
                              if (!group.sources?.length) return null;
                              
                              // Group sources by category
                              const sourcesByCategory = group.sources.reduce((acc, source) => {
                                const category = source.category || "Product Documents";
                                if (!acc[category]) acc[category] = [];
                                acc[category].push(source);
                                return acc;
                              }, {});

                              const categories = Object.keys(sourcesByCategory);
                              
                              return categories.map((category) => (
                                <button
                                  key={category}
                                  onClick={() => setSearchSubMode(category)}
                                  className={`px-4 py-2 text-sm font-medium transition ${
                                    searchSubMode === category
                                      ? "border-b-2 border-[#06b6d4] text-[#06b6d4]"
                                      : "text-gray-500 hover:text-[#253439]"
                                  }`}
                                >
                                  {categoryDisplayNames[category] || category} ({sourcesByCategory[category].length})
                                </button>
                              ));
                            })()}
                          </div>

                          {/* Sources Placeholder Blocks - Only show for Answer tab - matching ChatWindow */}
                          {searchSubMode === "rag" && (
                            <div className="mb-6">
                              <div className="flex flex-wrap gap-3 mb-4">
                                {group.sources?.length > 0 &&
                                  group.sources.slice(0, 4)
                                    .map((source, i) => (
                                      <a
                                        key={i}
                                        href={source.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-600 border hover:bg-gray-100 transition max-w-[160px]"
                                      >
                                        {source.icon === "pdf" ? (
                                          <div className={`${getCategoryColor(source.category || "Product Documents").iconColor} mr-2`}>
                                            <svg
                                              width="12"
                                              height="12"
                                              fill="currentColor"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                              viewBox="0 0 24 24"
                                            >
                                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                              <polyline points="14,2 14,8 20,8" />
                                            </svg>
                                          </div>
                                        ) : (
                                          <div className={`w-4 h-4 ${getCategoryColor(source.category || "Product Documents").bg.replace('bg-', 'bg-').replace('-100', '-300')} rounded mr-2`}></div>
                                        )}
                                        <span className="truncate">{source.name || "PDF"}</span>
                                      </a>
                                    ))}
                              </div>
                            </div>
                          )}

                          {/* Sources Content Display - matching ChatWindow */}
                          <div className="max-w-full overflow-hidden">
                            {(() => {
                              // Helper function to render sources - matching ChatWindow
                              const renderSources = (sourcesToRender, showCategoryBadge = true, showTopSourceSummary = false) => (
                                <div className="space-y-3 w-full max-w-full">
                                  {sourcesToRender.map((source, i) => {
                                    const categoryColors = getCategoryColor(source.category || "Product Documents");
                                    return (
                                    <div key={i} className="w-full max-w-full">
                                      <a
                                        href={source.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-start p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition w-full max-w-full"
                                      >
                                        <div className={`w-8 h-8 ${categoryColors.bg} rounded-lg flex items-center justify-center mr-3 flex-shrink-0`}>
                                          {source.icon === "pdf" ? (
                                            <svg
                                              width="14"
                                              height="14"
                                              fill="currentColor"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                              viewBox="0 0 24 24"
                                              className={categoryColors.iconColor}
                                            >
                                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                              <polyline points="14,2 14,8 20,8" />
                                              <line x1="16" y1="13" x2="8" y2="13" />
                                              <line x1="16" y1="17" x2="8" y2="17" />
                                              <polyline points="10,9 9,9 8,9" />
                                            </svg>
                                          ) : (
                                            <div className={`w-4 h-4 ${categoryColors.bg.replace('bg-', 'bg-').replace('-100', '-300')} rounded`}></div>
                                          )}
                                        </div>
                                        <div className="flex-1 min-w-0 overflow-hidden">
                                          <h3 className="text-sm font-medium text-gray-900 truncate">
                                            {source.name || "PDF"}
                                          </h3>
                                          <p className="text-xs text-gray-500 mt-1 line-clamp-2 break-words">
                                            {source.summary || "No summary available"}
                                          </p>
                                          {showCategoryBadge && (
                                            <div className="mt-2">
                                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${categoryColors.bg} ${categoryColors.text}`}>
                                                {source.category || "Product Documents"}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </a>
                                      
                                      {/* AI-Generated Summary for Top Source in Category Tabs - matching ChatWindow */}
                                      {showTopSourceSummary && i === 0 && (() => {
                                        // Extract document filename from link
                                        const documentPath = source.link?.split('/').pop();
                                        const aiSummary = documentSummaries[documentPath];
                                        
                                        // Request summary if not already available
                                        if (!aiSummary && documentPath) {
                                          requestDocumentSummary(documentPath);
                                        }
                                        
                                        return (
                                          <div className="mt-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                                            <div className="flex items-start">
                                              <div className="flex-shrink-0">
                                                <svg className="h-4 w-4 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                              </div>
                                              <div className="ml-2 flex-1">
                                                <h4 className="text-sm font-medium text-blue-900">AI Summary</h4>
                                                <div className="mt-1 text-sm text-blue-800">
                                                  {aiSummary ? (
                                                    <p className="leading-relaxed">{aiSummary}</p>
                                                  ) : (
                                                    <div className="flex items-center space-x-2">
                                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                      <span className="text-xs text-blue-600">Generating summary...</span>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  );
                                })}
                              </div>
                            );

                              // Handle different tab modes - matching ChatWindow
                              if (searchSubMode === "sources") {
                                // Show all sources
                                return (
                                  <div className="mb-6">
                                    {group.sources?.length > 0 ? (
                                      renderSources(group.sources, true)
                                    ) : (
                                      <div className="text-center py-8 text-gray-500">
                                        <svg
                                          width="48"
                                          height="48"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="1"
                                          viewBox="0 0 24 24"
                                          className="mx-auto mb-3"
                                        >
                                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                          <polyline points="14,2 14,8 20,8" />
                                        </svg>
                                        <p>No sources available yet</p>
                                        <p className="text-xs mt-1">Sources will appear after you search</p>
                                      </div>
                                    )}
                                  </div>
                                );
                              } else if (searchSubMode !== "rag" && group.sources?.length > 0) {
                                // Show category-specific sources - matching ChatWindow
                                const categoryName = searchSubMode;
                                const categorySources = group.sources.filter(
                                  source => (source.category || "Product Documents") === categoryName
                                );

                                return (
                                  <div className="mb-6">
                                    {categorySources.length > 0 && (
                                      <div>
                                        {/* Top Source with AI Summary - matching ChatWindow */}
                                        <div className="mb-6 p-5 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-100 shadow-sm max-w-[85%] self-start overflow-hidden">
                                          <div className="flex items-center mb-3">
                                            <h4 className="text-lg font-semibold text-gray-900 mr-2">Overview</h4>
                                            <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">Generated</div>
                                          </div>
                                          <div className="text-gray-700 leading-relaxed">
                                            {(() => {
                                              const firstSource = categorySources[0];
                                              const documentPath = firstSource.link?.split('/').pop();
                                              const aiSummary = documentSummaries[documentPath];
                                              if (!aiSummary && documentPath) requestDocumentSummary(documentPath);
                                              return aiSummary ? <div className="prose prose-sm max-w-none"><p>{aiSummary}</p></div> : (
                                                <div className="flex items-center space-x-3">
                                                  <div className="flex space-x-1">
                                                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                                                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                                                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                                                  </div>
                                                  <span className="text-sm text-gray-600">Generating overview...</span>
                                                </div>
                                              );
                                            })()}
                                          </div>
                                        </div>
                                        {/* Top Source Card - matching ChatWindow */}
                                        <div className="w-full">
                                          <a
                                            href={categorySources[0].link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-start p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition w-full max-w-[85%] overflow-hidden mb-2"
                                          >
                                            <div className={`${getCategoryColor(categorySources[0].category || "Product Documents").iconColor} mr-3 mt-1 flex-shrink-0`}>
                                              <svg width="16" height="16" fill="currentColor" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14,2 14,8 20,8" />
                                              </svg>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <h3 className="text-sm font-medium text-gray-900 mb-1 truncate">{categorySources[0].name || "PDF"}</h3>
                                              <p className="text-xs text-gray-500 leading-relaxed" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>{categorySources[0].summary || "No summary available"}</p>
                                            </div>
                                          </a>
                                        </div>
                                        {/* Other Related Source Section - matching ChatWindow */}
                                        {categorySources.length > 1 && (
                                          <div className="mt-4">
                                            <div className="text-xs font-semibold text-gray-600 mb-2">Other Related Source</div>
                                            <div className="space-y-2">
                                              {categorySources.slice(1).map((source, i) => (
                                                <div key={i} className="w-full">
                                                  <a
                                                    href={source.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-start p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition w-full max-w-[85%] overflow-hidden"
                                                  >
                                                    <div className={`${getCategoryColor(source.category || "Product Documents").iconColor} mr-3 mt-1 flex-shrink-0`}>
                                                      <svg width="16" height="16" fill="currentColor" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                        <polyline points="14,2 14,8 20,8" />
                                                      </svg>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                      <h3 className="text-sm font-medium text-gray-900 mb-1 truncate">{source.name || "PDF"}</h3>
                                                      <p className="text-xs text-gray-500 leading-relaxed" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>{source.summary || "No summary available"}</p>
                                                    </div>
                                                  </a>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              return null;
                            })()}
                          </div>
                        </div>

                        {/* Search Results Area - Only show in Answer tab - matching ChatWindow */}
                        {searchSubMode === "rag" && group.responses.length > 0 && (
                          <div className="space-y-4">
                            {group.responses.map((msg, idx) => (
                              <div key={idx} className="w-full flex flex-col mb-4 items-start">
                                <div className="px-5 py-4 rounded-2xl max-w-[85%] break-words border shadow-sm bg-white text-cyan-900 border-cyan-300">
                                  <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text, msg.isSearchResponse) }} />
                                </div>
                                <div className="mt-1 text-xs text-gray-400 font-medium">
                                  {msg.sender === "bot" ? `${formatTimestamp(msg.timestamp || Date.now())}` : `${msg.sender || ""} ${formatTimestamp(msg.timestamp || Date.now())}`}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </main>
          </div>
        </div>

        {/* Fade gradient above chat input - matching ChatWindow */}
        <div
          className="fixed pointer-events-none"
          style={{
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: '64rem', // Match max-w-4xl
            height: '40px', // Reduced height
            bottom: '120px', // Adjusted position
            background: 'linear-gradient(to top, white 0%, white 40%, transparent 100%)',
            zIndex: 40
          }}
        />

        {/* Fixed Chat Input and Utility Bar - matching ChatWindow */}
        <div className="fixed bottom-0 left-1/2 transform -translate-x-1/2 bg-white z-50 w-full max-w-4xl">
          <div className="w-full mx-auto px-6 pb-4">
            {/* Chat Input - matching ChatWindow */}
            <div className="bg-white rounded-xl border border-[#06b6d4] shadow-md flex items-start px-6 py-4">
              <textarea
                className="flex-1 bg-transparent border-none outline-none text-[#253439] placeholder-[#A2A9AF] focus:ring-0 resize-none overflow-y-auto"
                style={{ 
                  minHeight: '28px', 
                  maxHeight: '200px', 
                  height: '28px',
                  fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
                  fontSize: "14px"
                }}
                placeholder="🔍 Search knowledge base..."
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  autoResizeTextarea(e.target);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendSearchMessage();
                  }
                }}
              />
              {/* Send Button - matching ChatWindow */}
              <button
                className="ml-2 bg-[#06b6d4] hover:bg-cyan-700 rounded-lg p-2 transition"
                aria-label="Send"
                onClick={sendSearchMessage}
                type="button"
              >
                <svg width="20" height="20" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M22 2 11 13" />
                  <path d="M22 2l-7 20-4-9-9-4z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TSEAgentRag;